import {
  ComponentProps,
  CSSProperties,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { computeStyle } from "./computeStyle";
import { MENU_PORTAL_ATTR } from "./constants";

// Минимальный набор пропсов react-select MenuPortalProps, которые мы используем.
// MenuPortalProps не реэкспортится из пакета, но компонент контравариантен по
// пропсам — этого подмножества достаточно для components.MenuPortal.
interface CategoryMenuPortalProps {
  appendTo?: HTMLElement;
  controlElement: HTMLDivElement | null;
  children: ReactNode;
  innerProps?: ComponentProps<"div">;
}

export const CategoryMenuPortal = ({
  appendTo,
  controlElement,
  children,
  innerProps,
}: CategoryMenuPortalProps) => {
  const [style, setStyle] = useState<CSSProperties | null>(null);

  const update = useCallback(() => {
    if (controlElement) setStyle(computeStyle(controlElement));
  }, [controlElement]);

  // Меню следует за контролом при скролле/ресайзе. scroll слушаем в capture-фазе
  // (3-й аргумент true), чтобы ловить скролл любого контейнера, а не только window.
  // Это стреляет десятками событий в кадр, а update делает getBoundingClientRect
  // (синхронный reflow) + setStyle — поэтому схлопываем серию в один пересчёт за
  // кадр через requestAnimationFrame.
  useLayoutEffect(() => {
    update();

    let frame: number | null = null;
    const scheduleUpdate = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        update();
      });
    };

    window.addEventListener("scroll", scheduleUpdate, true);
    window.addEventListener("resize", scheduleUpdate);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scheduleUpdate, true);
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [update]);

  if (!appendTo || !style) return null;

  return createPortal(
    <div {...innerProps} {...{ [MENU_PORTAL_ATTR]: "" }} style={style}>
      {children}
    </div>,
    appendTo,
  );
};
